import paths from '~/pages/paths';
import { Application } from '~/types/Application';
import { isAgent, isNogoManager } from '~/utils/getDecodedJwt';
import { handleGetutm } from '~/utils/handleGetUtm';

// nogo-manager reuses the agent review flow (read-only) to reach the contract/pricing
// decision page, so it should be routed the same way an agent is.
const isReviewer = () => isAgent() || isNogoManager();

export const getActiveStepPath = (application: any): string => {
  const utmParams = handleGetutm(application);

  switch (application.stage) {
    case 'companyType':
      return paths.companyType._;
    case 'highRisk':
      return paths.getInTouch._;
    case 'terminated':
    case 'fillApplication': {
      const stepKey = application.stagePayload.activeStep.key;
      const stepPath = application.stagePayload.activeStep.pathKey;
      switch (stepKey) {
        case 'services':
          return paths.wizard.services;

        default:
          if (stepKey?.startsWith('application')) {
            return `${paths.wizard.application}/${stepPath}`;
          }
          return paths.wizard._;
      }
    }
    case 'waitingForScreening':
      return paths.stageResult.waitingForScreening;
    case 'error':
      return paths.stageResult.error;
    case 'screeningError':
      return paths.stageResult.screeningError;
    case 'screeningReject':
    case 'salesforceReject':
      return paths.stageResult.screeningReject;
    case 'salesforceSoftDecline':
      return paths.stageResult.salesforceSoftDecline;
    case 'salesforceHardDecline':
      return paths.stageResult.salesforceHardDecline;
    case 'salesforceError':
      return paths.stageResult.salesforceError;
    case 'salesforceTechnicalError':
      return paths.stageResult.salesforceTechnicalError;
    case 'submit':
      return paths.stageResult.submit;
    case 'successfulOnboarding':
      return paths.stageResult.successfulOnboarding;
    case 'completed':
      if (isReviewer()) {
        return `${paths.ksa.review}/screeningResult`;
      }
      return paths.stageResult.completed;

    case 'contract':
      return paths.wizard.signContract;
    case 'payment':
      return paths.wizard.payment;

    // KSA Application stage — route based on activeStep.key
    case 'ksaApplication': {
      const stepKey = application.stagePayload?.activeStep?.key;
      const stepPath = application.stagePayload?.activeStep?.pathKey;

      // Step 2: Backend signals user should fill Initial Information
      if (stepKey === 'initialInformation') {
        return `${paths.ksa.initialInformation}?${utmParams}`;
      }

      // Step 1: Backend signals user should select products
      if (stepKey === 'services') {
        return `${paths.ksa.services}?${utmParams}`;
      }

      // Step 3+: Business Information sub-steps
      if (stepKey?.startsWith('application')) {
        return `${paths.ksa.application}/${stepPath}?${utmParams}`;
      }

      return paths.ksa._;
    }
    case 'ksaReview': {
      if (application.stagePayload?.activeStep) {
        // for the agent
        const stepKey = application.stagePayload.activeStep.key;
        const stepPath = application.stagePayload.activeStep.pathKey;
        switch (stepKey) {
          default:
            if (stepKey?.startsWith('review')) {
              return `${paths.ksa.review}/${stepPath}`;
            }
            return paths.ksa._;
        }
      } else {
        // for merchant
        return paths.ksa.review;
      }
    }
    case 'ksaReviewResubmit':
      return paths.ksa.reviewResubmit;
    case 'ksaReviewReject':
      if (isReviewer()) {
        return paths.ksa.review;
      }
      return paths.ksa.reviewReject;
    case 'ksaTerminated':
      if (isReviewer()) {
        return paths.ksa.review;
      }
      return paths.ksa.terminated;
    case 'ksaReviewSuccess':
      return paths.ksa.reviewSuccess;
    case 'ksaServices': {
      const stepKey = application.stagePayload?.activeStep?.key;

      // After product selection the backend keeps the app on ksaServices but
      // advances the active step to initialInformation; route there instead of
      // falling back to the (blank) product selection page.
      if (stepKey === 'initialInformation') {
        return `${paths.ksa.initialInformation}?${utmParams}`;
      }

      return `${paths.ksa.services}?${utmParams}`;
    }
    case 'ksaContract':
      if (isReviewer()) {
        return paths.ksa.review;
      }
      return `${paths.ksa.contract}?${utmParams}`;
    case 'ksaPayment':
      return `${paths.ksa.payment}?${utmParams}`;
    case 'ksaAutomatedOnboarding':
    case 'ksaTechFailedOnboarding':
    case 'ksaFailedOnboarding':
    case 'ksaManualOnboarding':
    case 'ksaCompletedOnboarding':
      if (isReviewer()) {
        return `${paths.ksa.review}/screeningResult`;
      }
      return `${paths.ksa.completion}?${utmParams}`;

    default:
      return paths.stageResult.unknownStage;
  }
};
